import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  FaEdit,
  FaUsers,
  FaTrash,
  FaTimes,
  FaUserPlus,
  FaSearch,
  FaInfo,
  FaPlus,
  FaCheck,
  FaUserClock,
  FaEye,
  FaEyeSlash,
  FaEnvelope,
  FaUpload,
  FaPaste,
  FaFileAlt,
  FaCopy,
  FaArrowLeft,
  FaArrowRight,
} from "react-icons/fa";
import debounce from "lodash.debounce";
import moment from "moment-timezone";
import "./TeamButtons.css";

import { Button } from "../../../shadcn/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../shadcn/ui/dialog";
import { Input } from "../../../shadcn/ui/input";
import { Label } from "../../../shadcn/ui/label";
import { Textarea } from "../../../shadcn/ui/textarea";
import { Badge } from "../../../shadcn/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "../../../shadcn/ui/avatar";

import { teamsService } from "../../../services/host-apiServices/teamsService";
import { apiV2Service } from "../../../services/apiV2Service";
import geminiService from "../../../services/geminiService";
import logger from "../../../utils/logger";
import { showApiMessage } from "../../../utils/messageHandler";
import * as localService from "../../../services/localService";
import toastUtils from "../../../utils/toast";
import { FeatureAccess } from "../../../utils/featureAccessControl";

const Groups = () => {
  const [myTeams, setMyTeams] = useState([]);
  const [otherTeams, setOtherTeams] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmails, setInviteEmails] = useState("");
  const [extractedEmails, setExtractedEmails] = useState([]);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [extractLoading, setExtractLoading] = useState(false);
  const [groupName, setGroupName] = useState("");
  const [groupDescription, setGroupDescription] = useState("");
  const [groupMembers, setGroupMembers] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentUserRole, setCurrentUserRole] = useState("");
  const [currentUserId, setCurrentUserId] = useState(null);
  const [showDeclinedInvitations, setShowDeclinedInvitations] = useState(false);
  const [activeTab, setActiveTab] = useState("my-teams");
  const [emailInputMode, setEmailInputMode] = useState("manual");
  const [validationErrors, setValidationErrors] = useState([]);
  const [copySuccess, setCopySuccess] = useState(false);
  const fileInputRef = useRef(null);
  const [myTeamsPage, setMyTeamsPage] = useState(0);
  const [otherTeamsPage, setOtherTeamsPage] = useState(0);
  const [pageSize] = useState(10);
  const [totalMyTeamsPages, setTotalMyTeamsPages] = useState(0);
  const [totalOtherTeamsPages, setTotalOtherTeamsPages] = useState(0);
  const [totalMyTeamsElements, setTotalMyTeamsElements] = useState(0);
  const [totalOtherTeamsElements, setTotalOtherTeamsElements] = useState(0);
  const [membersPage, setMembersPage] = useState(0);
  const [totalMembersPages, setTotalMembersPages] = useState(0);
  const [membersPageSize] = useState(10);

  const currentUser = localService.getUser();
  const hasTeamsAccess = FeatureAccess.hasTeamsAccess(currentUser);

  useEffect(() => {
    if (!hasTeamsAccess) return;
    fetchGroups();
    const user = localService.getUser();
    setCurrentUserRole(user?.role || "");
    setCurrentUserId(user?.id || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myTeamsPage, otherTeamsPage, pageSize, activeTab, hasTeamsAccess]);

  const isAdministrator = () => {
    return currentUserRole === "ROLE_ADMINISTRATOR" || currentUserRole === "ROLE_SUBSCRIBER_OWNER";
  };

  const isIndividualSubscriber = () => {
    return currentUserRole === "ROLE_SUBSCRIBER_INDIVIDUAL";
  };

  const fetchGroups = async () => {
    setIsLoading(true);
    try {
      const isMyTeams = activeTab === "my-teams";
      const ownership = isMyTeams ? "CREATED" : "JOINED";
      const currentPage = isMyTeams ? myTeamsPage : otherTeamsPage;

      // Fetch teams with pagination using new teamsService
      const response = await teamsService.getTeams({
        page: currentPage,
        size: pageSize,
        paged: true,
        ownership: ownership,
      });

      if (response.status === 200 && response.data) {
        const teams = response.data.content || [];
        const totalPages =
          response.data.totalPages !== undefined
            ? response.data.totalPages
            : Math.ceil((response.data.totalElements || 0) / pageSize);
        const totalElements = response.data.totalElements || 0;

        if (isMyTeams) {
          setMyTeams(teams);
          setTotalMyTeamsPages(totalPages);
          setTotalMyTeamsElements(totalElements);
        } else {
          setOtherTeams(teams);
          setTotalOtherTeamsPages(totalPages);
          setTotalOtherTeamsElements(totalElements);
        }

        logger.log(`Fetched ${teams.length} teams for tab: ${activeTab} with ownership: ${ownership}`);
      }
    } catch (error) {
      logger.error("Error fetching groups:", error);
      showApiMessage(error, "load teams");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchGroupMembers = async groupId => {
    try {
      // Get team by id to fetch members
      const response = await teamsService.getTeamById(groupId);
      if (response.status === 200 && response.data) {
        const members = response.data.members || [];

        // Set pagination info for members (members are included in team response, not paginated separately)
        setTotalMembersPages(1);

        setGroupMembers(prev => ({
          ...prev,
          [groupId]: members,
        }));
        return members;
      }
      return [];
    } catch (error) {
      logger.error(`Error fetching members for group ${groupId}:`, error);
      showApiMessage(error, "fetch team members");
      return [];
    }
  };

  // New function to handle member pagination
  const handleMembersPageChange = async newPage => {
    if (newPage >= 0 && newPage < totalMembersPages && selectedGroup) {
      setMembersPage(newPage);
      await fetchGroupMembers(selectedGroup.id);
    }
  };

  // New functions to handle team pagination
  const handleMyTeamsPageChange = newPage => {
    if (newPage >= 0 && newPage < totalMyTeamsPages) {
      setMyTeamsPage(newPage);
    }
  };

  const handleOtherTeamsPageChange = newPage => {
    if (newPage >= 0 && newPage < totalOtherTeamsPages) {
      setOtherTeamsPage(newPage);
    }
  };

  const handleCreateGroup = async () => {
    if (!groupName.trim()) return;

    setIsLoading(true);
    try {
      logger.log("Creating team:", groupName);
      const response = await teamsService.createTeam({
        name: groupName,
        description: groupDescription.trim() || "",
        members: [],
      });
      logger.log("Team creation response:", response);

      // Close modal regardless of specific status code as long as it's successful
      if (response.status >= 200 && response.status < 300) {
        const newGroup = response.data;
        if (newGroup) {
          setMyTeams(prev => [...prev, newGroup]);
        }

        toastUtils.success(`Team Created`, {
          description: `"${groupName}" has been created successfully`,
        });

        // Force clearing input and closing modal
        setTimeout(() => {
          setGroupName("");
          setGroupDescription("");
          setShowCreateModal(false);
        }, 100);

        // Fetch updated list of teams to ensure UI is in sync
        fetchGroups();
      } else {
        throw new Error(`Unexpected response status: ${response.status}`);
      }
    } catch (error) {
      logger.error("Error creating team:", error);
      showApiMessage(error, "create team");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteGroup = async () => {
    if (!selectedGroup) return;

    setIsLoading(true);
    try {
      await teamsService.deleteTeam(selectedGroup.id);
      setMyTeams(prev => prev.filter(group => group.id !== selectedGroup.id));
      setOtherTeams(prev => prev.filter(group => group.id !== selectedGroup.id));
      toastUtils.success(`Team Deleted`, {
        description: `"${selectedGroup.name}" has been deleted successfully`,
      });
      setShowDeleteModal(false);
      setSelectedGroup(null);
    } catch (error) {
      logger.error("Error deleting team:", error);
      showApiMessage(error, "delete team");
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateGroupName = async () => {
    if (!selectedGroup || !groupName.trim()) return;

    setIsLoading(true);
    try {
      await teamsService.updateTeam(selectedGroup.id, {
        name: groupName,
        description: groupDescription.trim() || "",
      });
      const updateTeams = teams =>
        teams.map(group =>
          group.id === selectedGroup.id
            ? { ...group, name: groupName, description: groupDescription.trim() || "" }
            : group
        );

      setMyTeams(updateTeams);
      setOtherTeams(updateTeams);

      toastUtils.success(`Team Updated`, {
        description: `Team renamed to "${groupName}" successfully`,
      });
      setShowCreateModal(false);
    } catch (error) {
      logger.error("Error updating team name:", error);
      showApiMessage(error, "update team name");
    } finally {
      setIsLoading(false);
    }
  };

  const handleInviteUsers = async () => {
    if (!selectedGroup || !inviteEmails.trim()) return;

    // Validate emails
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const emails = inviteEmails
      .split(/[,\n]/)
      .map(email => email.trim())
      .filter(email => email.length > 0);

    // Check for invalid emails
    const invalid = emails.filter(email => !emailRegex.test(email));
    if (invalid.length > 0) {
      setValidationErrors(invalid);
      const syntheticError = {
        response: {
          status: 400,
          data: {
            message: `${invalid.length} email addresses are invalid. Please correct them.`,
          },
        },
      };
      showApiMessage(syntheticError, "invite users");
      return;
    }

    setValidationErrors([]);
    setInviteLoading(true);
    try {
      // Get current team members
      const currentTeam = await teamsService.getTeamById(selectedGroup.id);
      const existingMembers = currentTeam.data?.members || [];
      const existingUserIds = new Set(existingMembers.map(m => m.user?.id || m.id || m.userId).filter(Boolean));

      // Search for users by email to get their user IDs
      const currentUser = localService.getUser();
      const organizationId = currentUser?.organizationId || currentUser?.user?.organizationId;

      const usersToAdd = [];
      const notFoundEmails = [];

      for (const email of emails) {
        try {
          const searchResponse = await apiV2Service.searchUsers({
            q: email,
            limit: 10,
            organizationId: organizationId,
            status: "ACTIVE",
          });

          // Handle response structure
          let users = [];
          if (Array.isArray(searchResponse?.data?.data)) {
            users = searchResponse.data.data;
          } else if (Array.isArray(searchResponse?.data)) {
            users = searchResponse.data;
          }

          // Find user with matching email
          const user = users.find(
            u => u.emailAddress?.toLowerCase() === email.toLowerCase() || u.email?.toLowerCase() === email.toLowerCase()
          );

          if (user) {
            const userId = user.id || user.userId;
            if (userId && !existingUserIds.has(userId)) {
              usersToAdd.push({
                userId: userId,
                role: "MEMBER", // Default role for new members
              });
              existingUserIds.add(userId); // Prevent duplicates
            }
          } else {
            notFoundEmails.push(email);
          }
        } catch (error) {
          logger.error(`Error searching for user with email ${email}:`, error);
          notFoundEmails.push(email);
        }
      }

      if (notFoundEmails.length > 0) {
        toastUtils.warning("Some users not found", {
          description: `Could not find users with emails: ${notFoundEmails.join(", ")}`,
        });
      }

      if (usersToAdd.length === 0) {
        toastUtils.info("No new members to add", {
          description: "All users are already members or could not be found.",
        });
        setInviteLoading(false);
        return;
      }

      // Combine existing members with new members
      const allMembers = [
        ...existingMembers.map(m => ({
          userId: m.user?.id || m.id || m.userId,
          role: m.role || "MEMBER",
        })),
        ...usersToAdd,
      ];

      // Update team with new members using PATCH
      await teamsService.updateTeam(selectedGroup.id, {
        members: allMembers,
      });

      const syntheticSuccess = {
        status: 200,
        data: {
          message: `${usersToAdd.length} member(s) added to "${selectedGroup.name}" successfully`,
        },
      };
      showApiMessage(syntheticSuccess, "add members");

      setShowInviteModal(false);
      setInviteEmails("");
      setExtractedEmails([]);
      setEmailInputMode("manual");

      // Refresh group members
      fetchGroupMembers(selectedGroup.id);

      // Refresh teams list
      fetchGroups();
    } catch (error) {
      logger.error("Error adding users to team:", error);
      showApiMessage(error, "add members");
    } finally {
      setInviteLoading(false);
    }
  };

  const handleEditGroup = group => {
    setSelectedGroup(group);
    setGroupName(group.name);
    setGroupDescription(group.description || "");
    setShowCreateModal(true);
  };

  const handleManageMembers = async group => {
    // Make sure other modals are closed
    setShowCreateModal(false);
    setShowInviteModal(false);
    setShowDeleteModal(false);

    // Reset members pagination
    setMembersPage(0);
    setTotalMembersPages(0);

    // Set up for members modal
    setSelectedGroup(group);
    setShowMembersModal(true);
    setShowDeclinedInvitations(false);

    // Clear search state
    setSearchTerm("");
    setSearchResults([]);

    const members = await fetchGroupMembers(group.id);
    setSelectedMembers(members || []);
  };

  const handleInvite = group => {
    setSelectedGroup(group);
    setInviteEmails("");
    setExtractedEmails([]);
    setEmailInputMode("manual");
    setValidationErrors([]);
    setShowInviteModal(true);
  };

  // Use inline function to avoid dependency issues
  const searchUsers = useCallback(term => {
    const debouncedSearch = debounce(async searchTerm => {
      if (!searchTerm || searchTerm.length < 3) {
        setSearchResults([]);
        setIsSearching(false);
        return;
      }

      setIsSearching(true);
      try {
        const currentUser = localService.getUser();
        const organizationId = currentUser?.organizationId || currentUser?.user?.organizationId;

        const response = await apiV2Service.searchUsers({
          q: searchTerm,
          limit: 50,
          organizationId: organizationId,
          status: "ACTIVE",
        });

        // Handle response structure - could be response.data.data or response.data
        let users = [];
        if (Array.isArray(response?.data?.data)) {
          users = response.data.data;
        } else if (Array.isArray(response?.data)) {
          users = response.data;
        }

        setSearchResults(users);
      } catch (error) {
        logger.error("Error searching users:", error);
        toastUtils.error("Search failed", {
          description: "Failed to search for users",
        });
      } finally {
        setIsSearching(false);
      }
    }, 500);

    debouncedSearch(term);
  }, []);

  const handleSearchChange = e => {
    const value = e.target.value;
    setSearchTerm(value);

    // If field is cleared, also clear results
    if (!value || value.trim() === "") {
      setSearchResults([]);
      return;
    }

    searchUsers(value);
  };

  const addMember = user => {
    // Handle both old format (user.id) and new format (user.user?.id)
    const userId = user.user?.id || user.id || user.userId;
    const memberId = member => member.user?.id || member.id || member.userId;

    if (!selectedMembers.some(member => memberId(member) === userId)) {
      setSelectedMembers([...selectedMembers, user]);
      const firstName = user.user?.firstName || user.firstName;
      const lastName = user.user?.lastName || user.lastName;
      const syntheticSuccess = {
        status: 200,
        data: { message: `${firstName} ${lastName} added` },
      };
      showApiMessage(syntheticSuccess, "add member");
    } else {
      const firstName = user.user?.firstName || user.firstName;
      const lastName = user.user?.lastName || user.lastName;
      const syntheticError = {
        response: {
          status: 400,
          data: {
            message: `${firstName} ${lastName} is already selected`,
          },
        },
      };
      showApiMessage(syntheticError, "add member");
    }

    // Clear search input and results after adding
    setSearchTerm("");
    setSearchResults([]);
  };

  const removeMember = user => {
    const userId = user.user?.id || user.id || user.userId;
    const memberId = member => member.user?.id || member.id || member.userId;
    setSelectedMembers(selectedMembers.filter(member => memberId(member) !== userId));

    const syntheticSuccess = {
      status: 200,
      data: { message: "Member removed successfully" },
    };
    showApiMessage(syntheticSuccess, "remove member");
  };

  const handleUpdateMembers = async () => {
    if (!selectedGroup) return;

    setIsLoading(true);
    try {
      // Get current members from cache
      const currentMembers = groupMembers[selectedGroup.id] || [];

      // Find members to add and remove
      const currentMemberIds = currentMembers.map(member => member.user?.id || member.id || member.userId);
      const selectedMemberIds = selectedMembers.map(member => member.user?.id || member.id || member.userId);

      // Build members array for update
      // Members to add (not in current, but in selected)
      const membersToAdd = selectedMembers
        .filter(member => {
          const memberId = member.user?.id || member.id || member.userId;
          return !currentMemberIds.includes(memberId);
        })
        .map(member => ({
          userId: member.user?.id || member.id || member.userId,
          role: member.role || "MEMBER",
        }));

      // Members to remove (in current, but not in selected)
      const membersToRemove = currentMembers
        .filter(member => {
          const memberId = member.user?.id || member.id || member.userId;
          return !selectedMemberIds.includes(memberId);
        })
        .map(member => ({
          userId: member.user?.id || member.id || member.userId,
          role: member.role || "MEMBER",
          remove: true,
        }));

      // Combine all member changes
      const allMemberChanges = [...membersToAdd, ...membersToRemove];

      // Update team with all member changes at once
      if (allMemberChanges.length > 0) {
        await teamsService.updateTeam(selectedGroup.id, {
          members: allMemberChanges,
        });
        logger.log(`Updated ${allMemberChanges.length} member changes in team ${selectedGroup.id}`);
      }

      // Update local cache
      setGroupMembers(prev => ({
        ...prev,
        [selectedGroup.id]: selectedMembers,
      }));

      const syntheticSuccess = {
        status: 200,
        data: { message: "Team members updated successfully" },
      };
      showApiMessage(syntheticSuccess, "update team members");

      // Close the modal and reset state
      setShowMembersModal(false);
      setSelectedMembers([]);

      // Refresh the members with latest data from server
      fetchGroupMembers(selectedGroup.id);

      // Refresh all groups to update counts
      fetchGroups();
    } catch (error) {
      logger.error("Error updating team members:", error);

      // More specific error message based on response if available
      const errorMessage = error.response?.data?.message || error.message || "Please try again later";
      const syntheticError = {
        response: {
          status: error.response?.status || 400,
          data: { message: errorMessage },
        },
      };
      showApiMessage(syntheticError, "update team members");
    } finally {
      setIsLoading(false);
    }
  };

  // Function to get invitation status badge
  const getStatusBadge = status => {
    // Only show invitation status badges to subscribers with individual role
    if (!status || (!isIndividualSubscriber() && !isAdministrator())) return null;

    // Only show badge for PENDING or DECLINED (if admin + show declined option enabled)
    switch (status) {
      case "ACCEPTED":
        // Don't show badge for accepted - this is the normal state
        return null;
      case "PENDING":
        return (
          <Badge
            variant="primary"
            className="px-2 py-1 ml-2 rounded-full bg-yellow-100 text-yellow-800 border-yellow-200"
          >
            <FaUserClock className="mr-1" size="10" />
            Pending Invitation
          </Badge>
        );
      case "DECLINED":
        return (
          <Badge variant="primary" className="px-2 py-1 ml-2 rounded-full bg-red-100 text-red-800 border-red-200">
            <FaTimes className="mr-1" size="10" />
            Declined Invitation
          </Badge>
        );
      default:
        return (
          <Badge variant="primary" className="ml-2 rounded-full">
            {status}
          </Badge>
        );
    }
  };

  // Function to get role badge
  const getRoleBadge = role => {
    if (!role) return null;

    switch (role) {
      case "OWNER":
        return (
          <Badge variant="primary" className="px-2 mr-2 rounded-full">
            Owner
          </Badge>
        );
      case "ADMIN":
        return (
          <Badge variant="primary" className="px-2 mr-2 rounded-full">
            Admin
          </Badge>
        );
      case "MEMBER":
        return (
          <Badge variant="primary" className="px-2 mr-2 rounded-full">
            Member
          </Badge>
        );
      default:
        return (
          <Badge variant="primary" className="px-2 mr-2 rounded-full">
            {role}
          </Badge>
        );
    }
  };

  // Filter members based on invitation status and admin role
  const filterMembers = members => {
    if (!members) return [];

    return members.filter(member => {
      // If not an admin, hide declined members
      if (!isAdministrator() && member.invitationStatus === "DECLINED") {
        return false;
      }

      // If admin but declined view is toggled off, hide declined members
      if (isAdministrator() && !showDeclinedInvitations && member.invitationStatus === "DECLINED") {
        return false;
      }

      return true;
    });
  };

  // Additional method to handle file upload for email extraction
  const handleFileUpload = async e => {
    const file = e.target.files[0];
    if (!file) return;

    setExtractLoading(true);
    try {
      const emails = await geminiService.extractEmailsFromFile(file);

      if (emails.length === 0) {
        toastUtils.warning("No emails found", {
          description: "No valid email addresses were found in the uploaded file.",
        });
        return;
      }

      // Format emails and add to textarea
      const formattedEmails = emails.join(", ");
      setExtractedEmails(emails);
      setInviteEmails(formattedEmails);

      toastUtils.success("Emails extracted", {
        description: `${emails.length} email addresses extracted from file.`,
      });
    } catch (error) {
      logger.error("Error extracting emails from file:", error);
      toastUtils.error("Email extraction failed", {
        description: error.message || "Failed to extract emails from the file.",
      });
    } finally {
      setExtractLoading(false);
    }
  };

  // Method to handle pasted text for email extraction
  const handlePastedText = async text => {
    if (!text.trim()) return;

    setExtractLoading(true);
    try {
      const emails = await geminiService.extractEmailsFromText(text);

      if (emails.length === 0) {
        toastUtils.warning("No emails found", {
          description: "No valid email addresses were found in the pasted text.",
        });
        return;
      }

      // Format emails and add to textarea
      const formattedEmails = emails.join(", ");
      setExtractedEmails(emails);
      setInviteEmails(formattedEmails);

      toastUtils.success("Emails extracted", {
        description: `${emails.length} email addresses extracted from text.`,
      });
    } catch (error) {
      logger.error("Error extracting emails from text:", error);
      toastUtils.error("Email extraction failed", {
        description: error.message || "Failed to extract emails from the text.",
      });
    } finally {
      setExtractLoading(false);
    }
  };

  // Method to handle paste from clipboard
  const handlePaste = async () => {
    try {
      const clipboardText = await navigator.clipboard.readText();
      if (clipboardText) {
        handlePastedText(clipboardText);
      } else {
        toastUtils.error("Empty clipboard", {
          description: "No text found in clipboard.",
        });
      }
    } catch (error) {
      logger.error("Error reading from clipboard:", error);
      toastUtils.error("Clipboard access failed", {
        description: "Unable to access clipboard. Please paste text manually.",
      });
    }
  };

  // Method to copy emails to clipboard
  const handleCopyEmails = () => {
    if (inviteEmails) {
      navigator.clipboard
        .writeText(inviteEmails)
        .then(() => {
          setCopySuccess(true);
          setTimeout(() => setCopySuccess(false), 2000);
          toastUtils.success("Copied to clipboard", {
            description: "Email addresses copied to clipboard",
          });
        })
        .catch(err => {
          logger.error("Error copying to clipboard:", err);
          toastUtils.error("Copy failed", {
            description: "Failed to copy to clipboard",
          });
        });
    }
  };

  // Pagination component for team lists (similar to AmenitiesManagement but without page size dropdown)
  const renderPagination = (currentPage, totalPages, onPageChange, totalElements) => {
    const actualTotalPages = Math.max(1, totalPages);

    return (
      <div className="flex flex-col gap-4 justify-between items-center px-4 py-3 bg-gray-50 border-t border-gray-200 sm:flex-row">
        <div className="text-sm text-gray-600">
          Showing {currentPage * pageSize + 1} to {Math.min((currentPage + 1) * pageSize, totalElements)} of{" "}
          {totalElements} results
        </div>

        <div className="flex gap-1 items-center">
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 0}
            className="flex gap-1 items-center px-3 py-1 text-sm rounded border hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FaArrowLeft className="w-3 h-3" />
            Previous
          </button>

          <div className="flex gap-1 items-center">
            {Array.from({ length: Math.min(5, actualTotalPages) }, (_, i) => {
              let pageNum;
              if (actualTotalPages <= 5) {
                pageNum = i;
              } else if (currentPage < 3) {
                pageNum = i;
              } else if (currentPage >= actualTotalPages - 2) {
                pageNum = actualTotalPages - 5 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }

              return (
                <button
                  key={pageNum}
                  onClick={() => onPageChange(pageNum)}
                  className={`px-3 py-1 text-sm border rounded ${
                    pageNum === currentPage ? "bg-blue-600 text-white border-blue-600" : "hover:bg-gray-100"
                  }`}
                >
                  {pageNum + 1}
                </button>
              );
            })}
          </div>

          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage >= actualTotalPages - 1}
            className="flex gap-1 items-center px-3 py-1 text-sm rounded border hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
            <FaArrowRight className="w-3 h-3" />
          </button>
        </div>
      </div>
    );
  };

  // Loading spinner component
  const LoadingSpinner = ({ size = "sm" }) => (
    <div
      className={`rounded-full border-2 border-gray-300 border-t-blue-600 animate-spin ${
        size === "sm" ? "w-4 h-4" : size === "md" ? "w-6 h-6" : "w-8 h-8"
      }`}
    />
  );

  const renderTeamList = teams => {
    if (isLoading && !teams.length) {
      return (
        <div className="flex justify-center items-center py-8">
          <LoadingSpinner size="md" />
          <span className="ml-2 text-sm text-gray-500">Loading teams...</span>
        </div>
      );
    }

    if (!teams.length) {
      return (
        <div className="py-8 text-center">
          <FaUsers className="mx-auto mb-3 text-gray-400" size={48} />
          <h5 className="mb-2 text-lg font-medium text-gray-700">No Teams Yet</h5>
          <p className="mb-4 text-sm text-gray-500">Create a team to get started</p>
          <button
            onClick={() => {
              setSelectedGroup(null);
              setGroupName("");
              setGroupDescription("");
              setShowCreateModal(true);
            }}
            className="inline-flex items-center px-4 py-2 text-sm text-white bg-blue-600 rounded-md transition-colors hover:bg-blue-700"
          >
            <FaPlus className="mr-2 w-4 h-4" />
            New Team
          </button>
        </div>
      );
    }

    return (
      <>
        <div className="divide-y divide-gray-200">
          {teams.map(group => (
            <div key={group.id} className="flex justify-between items-center px-4 py-3 hover:bg-gray-50">
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-900">{group.name}</div>
                {group.description && <div className="text-xs text-gray-600 mt-1">{group.description}</div>}
                <div className="flex items-center mt-1 text-xs text-gray-500">
                  <FaUsers className="mr-1 w-3 h-3" />
                  {group.members?.length || group.memberCount || 0} members
                  {group.createdAt && (
                    <span className="ml-3">Created: {moment(group.createdAt).format("MMM D, YYYY")}</span>
                  )}
                  {group.updatedAt && group.updatedAt !== group.createdAt && (
                    <span className="ml-3">Updated: {moment(group.updatedAt).format("MMM D, YYYY")}</span>
                  )}
                </div>
              </div>

              <div className="flex items-center ml-4 space-x-1">
                {isIndividualSubscriber() && (
                  <button
                    onClick={() => handleInvite(group)}
                    className="p-2 text-gray-400 rounded transition-colors hover:text-blue-600 hover:bg-blue-50"
                    title="Invite members"
                  >
                    <FaEnvelope className="w-4 h-4" />
                  </button>
                )}
                <button
                  onClick={() => handleManageMembers(group)}
                  className="p-2 text-gray-400 rounded transition-colors hover:text-blue-600 hover:bg-blue-50"
                  title="Manage members"
                >
                  <FaUserPlus className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleEditGroup(group)}
                  className="p-2 text-gray-400 rounded transition-colors hover:text-gray-600 hover:bg-gray-50"
                  title="Edit team"
                >
                  <FaEdit className="w-4 h-4" />
                </button>
                {activeTab === "my-teams" && (
                  <button
                    onClick={() => {
                      setSelectedGroup(group);
                      setShowDeleteModal(true);
                    }}
                    className="p-2 text-gray-400 rounded transition-colors hover:text-red-600 hover:bg-red-50"
                    title="Delete team"
                  >
                    <FaTrash className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Pagination Controls */}
        {((activeTab === "my-teams" && totalMyTeamsPages > 1) ||
          (activeTab === "other-teams" && totalOtherTeamsPages > 1)) && (
          <>
            {activeTab === "my-teams" &&
              totalMyTeamsPages > 1 &&
              renderPagination(myTeamsPage, totalMyTeamsPages, handleMyTeamsPageChange, totalMyTeamsElements)}
            {activeTab === "other-teams" &&
              totalOtherTeamsPages > 1 &&
              renderPagination(
                otherTeamsPage,
                totalOtherTeamsPages,
                handleOtherTeamsPageChange,
                totalOtherTeamsElements
              )}
          </>
        )}
      </>
    );
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-800">Team Management</h2>
          <p className="text-sm text-gray-600">Create and manage teams for collaboration and access control</p>
        </div>
        {activeTab === "my-teams" && (
          <button
            onClick={() => {
              setSelectedGroup(null);
              setGroupName("");
              setGroupDescription("");
              setShowCreateModal(true);
            }}
            className="inline-flex items-center px-4 py-2 text-sm text-white bg-blue-600 rounded-md transition-colors hover:bg-blue-700"
          >
            <FaPlus className="mr-2 w-4 h-4" />
            New Team
          </button>
        )}
      </div>

      {/* Tab Navigation */}
      <div className="overflow-hidden bg-white rounded-lg border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            <button
              onClick={() => setActiveTab("my-teams")}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === "my-teams"
                  ? "border-blue-600 text-blue-600 bg-blue-50"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              My Teams
            </button>
            <button
              onClick={() => setActiveTab("other-teams")}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === "other-teams"
                  ? "border-blue-600 text-blue-600 bg-blue-50"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              Other Teams
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-4">
          {activeTab === "my-teams" && renderTeamList(myTeams)}
          {activeTab === "other-teams" &&
            (isIndividualSubscriber() ? (
              otherTeams.length > 0 ? (
                renderTeamList(otherTeams)
              ) : (
                <div className="py-8 text-center">
                  <p className="text-sm text-gray-500">No teams joined</p>
                </div>
              )
            ) : otherTeams.length > 0 ? (
              renderTeamList(otherTeams)
            ) : (
              <div className="py-8 text-center">
                <p className="text-sm text-gray-500">No other teams</p>
              </div>
            ))}
        </div>
      </div>

      {/* Create/Edit Team Dialog */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{selectedGroup ? "Edit Team" : "Create New Team"}</DialogTitle>
            <DialogDescription>
              {selectedGroup ? "Update the team details" : "Enter details for your new team"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="team-name">Team Name *</Label>
              <Input
                id="team-name"
                type="text"
                placeholder="Enter team name"
                value={groupName}
                onChange={e => setGroupName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="team-description">Description (Optional)</Label>
              <Textarea
                id="team-description"
                placeholder="Enter team description"
                value={groupDescription}
                onChange={e => setGroupDescription(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={selectedGroup ? handleUpdateGroupName : handleCreateGroup}
              disabled={!groupName.trim() || isLoading}
            >
              {isLoading ? <LoadingSpinner size="sm" /> : selectedGroup ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage Team Members Dialog */}
      <Dialog open={showMembersModal} onOpenChange={setShowMembersModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Manage Team Members: {selectedGroup?.name}</DialogTitle>
            <DialogDescription>Search for users and add them to your team</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="search-users">Search Users</Label>
              <div className="relative">
                <div className="relative">
                  <Input
                    id="search-users"
                    type="text"
                    placeholder="Search by name or email"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    className="pr-10"
                    autoComplete="off"
                  />
                  {isSearching ? (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <LoadingSpinner size="sm" />
                    </div>
                  ) : (
                    <FaSearch className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  )}
                </div>
                {searchTerm.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white rounded-md border shadow-lg max-h-48 overflow-y-auto">
                    {searchResults.length > 0 ? (
                      searchResults.map(user => {
                        const userId = user.user?.id || user.id || user.userId;
                        const firstName = user.user?.firstName || user.firstName;
                        const lastName = user.user?.lastName || user.lastName;
                        const username = user.user?.username || user.username;

                        return (
                          <div
                            key={userId}
                            className="p-2 border-b flex justify-between items-center hover:bg-gray-50 cursor-pointer"
                            onClick={() => addMember(user)}
                          >
                            <div className="flex items-center gap-2 flex-1">
                              <Avatar className="h-8 w-8">
                                <AvatarImage
                                  src={user.user?.imageUrl || user.imageUrl}
                                  alt={`${firstName} ${lastName}`}
                                />
                                <AvatarFallback className="bg-blue-100 text-blue-700 text-xs">
                                  {firstName?.[0] || ""}
                                  {lastName?.[0] || ""}
                                </AvatarFallback>
                              </Avatar>
                              <div className="flex-1 min-w-0">
                                <div className="font-semibold">
                                  {firstName} {lastName}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {username || user.user?.email || user.email}
                                </div>
                                {(user.user?.email || user.email) && (user.user?.email || user.email) !== username && (
                                  <div className="text-xs text-gray-400">{user.user?.email || user.email}</div>
                                )}
                              </div>
                            </div>
                            <Button size="sm" variant="outline">
                              <FaUserPlus className="mr-1" /> Add
                            </Button>
                          </div>
                        );
                      })
                    ) : (
                      <div className="p-3 text-center text-gray-500">
                        {searchTerm.length < 3
                          ? "Type at least 3 characters to search"
                          : isSearching
                            ? "Searching..."
                            : "No results found"}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <p className="text-sm text-gray-500">Type at least 3 characters to search</p>
            </div>

            <div className="flex justify-between items-center">
              <h6 className="text-sm font-semibold">Selected Members ({filterMembers(selectedMembers).length})</h6>

              {isAdministrator() && (
                <Button
                  variant={showDeclinedInvitations ? "outline" : "secondary"}
                  size="sm"
                  onClick={() => setShowDeclinedInvitations(!showDeclinedInvitations)}
                >
                  {showDeclinedInvitations ? (
                    <>
                      <FaEyeSlash className="mr-1" /> Hide Declined
                    </>
                  ) : (
                    <>
                      <FaEye className="mr-1" /> Show Declined
                    </>
                  )}
                </Button>
              )}
            </div>

            <div className="p-2 rounded-md border max-h-72 overflow-y-auto">
              {selectedMembers.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  <FaInfo className="mb-2 mx-auto" size={24} />
                  <p className="text-sm">No members selected. Search and add members above.</p>
                </div>
              ) : filterMembers(selectedMembers).length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  <FaInfo className="mb-2 mx-auto" size={24} />
                  <p className="text-sm">
                    {isAdministrator() && !showDeclinedInvitations
                      ? "No active members found. Enable 'Show Declined' to view declined invitations."
                      : "No members found with current filters."}
                  </p>
                </div>
              ) : (
                filterMembers(selectedMembers).map(member => {
                  // Handle both old format and new format (with user object)
                  const user = member.user || member;
                  const memberId = member.user?.id || member.id || member.userId;
                  const firstName = user.firstName || "";
                  const lastName = user.lastName || "";
                  const username = user.username || "";
                  const email = user.email || "";
                  const imageUrl = user.imageUrl || null;
                  const joinedAt = member.joinedAt || "";

                  return (
                    <div
                      key={memberId}
                      className={`p-3 mb-2 flex justify-between items-center rounded-md ${
                        member.invitationStatus === "PENDING"
                          ? "bg-yellow-50 border border-yellow-200"
                          : member.invitationStatus === "DECLINED"
                            ? "bg-red-50 border border-red-200"
                            : "bg-gray-50 border border-gray-200"
                      }`}
                    >
                      <div className="flex items-start gap-3 flex-1">
                        <Avatar className="h-10 w-10">
                          <AvatarImage src={imageUrl} alt={`${firstName} ${lastName}`} />
                          <AvatarFallback className="bg-blue-100 text-blue-700">
                            {firstName?.[0] || ""}
                            {lastName?.[0] || ""}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold">
                            {firstName} {lastName}
                          </div>
                          <div className="text-sm text-gray-500">{username || email}</div>
                          {email && email !== username && <div className="text-xs text-gray-400 mt-0.5">{email}</div>}
                          {joinedAt && (
                            <div className="text-xs text-gray-400 mt-0.5">
                              Joined: {moment(joinedAt).format("MMM D, YYYY")}
                            </div>
                          )}
                          <div className="flex flex-wrap items-center mt-2 gap-2">
                            {getRoleBadge(member.role)}
                            {getStatusBadge(member.invitationStatus)}
                          </div>
                        </div>
                      </div>
                      <Button variant="outline" size="sm" onClick={() => removeMember(member)}>
                        <FaTimes className="mr-1" /> Remove
                      </Button>
                    </div>
                  );
                })
              )}
            </div>

            {/* Add members pagination */}
            {selectedGroup && totalMembersPages > 1 && (
              <div className="flex justify-center items-center gap-2 mt-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleMembersPageChange(membersPage - 1)}
                  disabled={membersPage === 0}
                >
                  <FaArrowLeft />
                </Button>
                {membersPage > 0 && (
                  <Button variant="outline" size="sm" onClick={() => handleMembersPageChange(0)}>
                    1
                  </Button>
                )}
                {membersPage > 1 && <span className="px-2">...</span>}
                <Button variant="default" size="sm">
                  {membersPage + 1}
                </Button>
                {membersPage < Math.max(1, totalMembersPages) - 2 && <span className="px-2">...</span>}
                {membersPage < Math.max(1, totalMembersPages) - 1 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleMembersPageChange(Math.max(1, totalMembersPages) - 1)}
                  >
                    {Math.max(1, totalMembersPages)}
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleMembersPageChange(membersPage + 1)}
                  disabled={membersPage >= Math.max(1, totalMembersPages) - 1}
                >
                  <FaArrowRight />
                </Button>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMembersModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateMembers} disabled={isLoading}>
              {isLoading ? <LoadingSpinner size="sm" /> : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invite Users Dialog */}
      <Dialog open={showInviteModal} onOpenChange={setShowInviteModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Invite Users to {selectedGroup?.name}</DialogTitle>
            <DialogDescription>Choose how you want to add email addresses</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex gap-2">
              <Button
                variant={emailInputMode === "manual" ? "default" : "outline"}
                onClick={() => setEmailInputMode("manual")}
              >
                <FaEnvelope className="mr-1" /> Manual Entry
              </Button>
              <Button
                variant={emailInputMode === "paste" ? "default" : "outline"}
                onClick={() => setEmailInputMode("paste")}
              >
                <FaPaste className="mr-1" /> Paste Text
              </Button>
              <Button
                variant={emailInputMode === "file" ? "default" : "outline"}
                onClick={() => setEmailInputMode("file")}
              >
                <FaUpload className="mr-1" /> Upload File
              </Button>
            </div>

            {emailInputMode === "manual" && (
              <div className="space-y-2">
                <Label htmlFor="email-addresses">Email Addresses</Label>
                <div className="flex gap-2">
                  <Textarea
                    id="email-addresses"
                    rows={4}
                    placeholder="Enter email addresses (comma or new line separated)"
                    value={inviteEmails}
                    onChange={e => setInviteEmails(e.target.value)}
                  />
                  {inviteEmails && (
                    <Button variant="outline" onClick={handleCopyEmails} title="Copy to clipboard" size="icon">
                      {copySuccess ? <FaCheck className="text-green-600" /> : <FaCopy />}
                    </Button>
                  )}
                </div>
                <div className="flex justify-between items-center text-sm">
                  <p className="text-gray-500">
                    Enter one email address per line or separate multiple emails with commas
                  </p>
                  {inviteEmails && (
                    <span className="text-gray-500">
                      {inviteEmails.split(/[,\n]/).filter(email => email.trim().length > 0).length} email(s)
                    </span>
                  )}
                </div>
              </div>
            )}

            {emailInputMode === "paste" && (
              <div className="space-y-3">
                <Label>Extract Emails from Pasted Text</Label>
                <Button variant="outline" className="w-full" onClick={handlePaste} disabled={extractLoading}>
                  {extractLoading ? (
                    <>
                      <LoadingSpinner size="sm" /> Processing...
                    </>
                  ) : (
                    <>
                      <FaPaste className="mr-1" /> Paste from Clipboard
                    </>
                  )}
                </Button>
                <Textarea
                  rows={3}
                  placeholder="Or manually paste text here and click 'Extract Emails'"
                  disabled={extractLoading}
                  onChange={() => {}}
                  onBlur={e => {
                    if (e.target.value.trim()) {
                      handlePastedText(e.target.value);
                    }
                  }}
                />
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={e => {
                    const textarea = e.target.previousSibling;
                    if (textarea && textarea.value.trim()) {
                      handlePastedText(textarea.value);
                    }
                  }}
                  disabled={extractLoading}
                >
                  {extractLoading ? (
                    <>
                      <LoadingSpinner size="sm" /> Processing...
                    </>
                  ) : (
                    <>
                      <FaSearch className="mr-1" /> Extract Emails
                    </>
                  )}
                </Button>

                {/* Help text explaining how it works */}
                <div className="px-3 py-2 rounded-md border bg-gray-50">
                  <p className="text-sm text-gray-600">
                    <FaInfo className="mr-1 inline" /> This feature uses AI to extract email addresses from any text. It
                    will analyze the content and extract only valid email addresses while filtering out duplicates and
                    invalid formats.
                  </p>
                </div>

                {extractedEmails.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <Label>Extracted Emails:</Label>
                      <Badge variant="primary" className="rounded-full">
                        {extractedEmails.length} found
                      </Badge>
                    </div>
                    <div className="flex gap-2">
                      <Textarea rows={4} value={inviteEmails} onChange={e => setInviteEmails(e.target.value)} />
                      <Button variant="outline" onClick={handleCopyEmails} title="Copy to clipboard" size="icon">
                        {copySuccess ? <FaCheck className="text-green-600" /> : <FaCopy />}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {emailInputMode === "file" && (
              <div className="space-y-3">
                <Label>Extract Emails from File</Label>
                <div className="p-4 text-center rounded-md border">
                  <FaFileAlt size={40} className="mb-3 mx-auto text-blue-600" />
                  <p>Upload a file to extract email addresses</p>
                  <p className="text-sm text-gray-500">Supports text files, CSVs, PDFs, images, and more</p>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                    className="hidden"
                    accept=".txt,.csv,.pdf,.jpg,.jpeg,.png,.doc,.docx"
                  />
                  <Button variant="outline" onClick={() => fileInputRef.current?.click()} disabled={extractLoading}>
                    {extractLoading ? (
                      <>
                        <LoadingSpinner size="sm" /> Processing...
                      </>
                    ) : (
                      <>
                        <FaUpload className="mr-1" /> Choose File
                      </>
                    )}
                  </Button>
                </div>

                {/* Help text explaining how it works */}
                <div className="px-3 py-2 rounded-md border bg-gray-50">
                  <p className="text-sm text-gray-600">
                    <FaInfo className="mr-1 inline" /> This feature uses AI Vision to analyze documents and images to
                    extract email addresses. For text files, it will extract any valid emails found in the text. For
                    non-text files like images, it will use AI to read and detect emails.
                  </p>
                </div>

                {extractedEmails.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <Label>Extracted Emails:</Label>
                      <Badge variant="primary" className="rounded-full">
                        {extractedEmails.length} found
                      </Badge>
                    </div>
                    <div className="flex gap-2">
                      <Textarea rows={4} value={inviteEmails} onChange={e => setInviteEmails(e.target.value)} />
                      <Button variant="outline" onClick={handleCopyEmails} title="Copy to clipboard" size="icon">
                        {copySuccess ? <FaCheck className="text-green-600" /> : <FaCopy />}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {validationErrors.length > 0 && (
              <div className="mt-3 p-3 rounded-md border border-red-200 bg-red-50">
                <p className="mb-2 font-semibold text-red-800">Invalid email addresses:</p>
                <ul className="list-disc list-inside text-sm text-red-700">
                  {validationErrors.map((email, index) => (
                    <li key={index}>{email}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Show a stats summary when we have emails */}
            {inviteEmails && (
              <div className="p-2 rounded-md border bg-gray-50">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Email addresses to invite:</span>
                  <span className="font-semibold">
                    {inviteEmails.split(/[,\n]/).filter(email => email.trim().length > 0).length}
                  </span>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInviteModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleInviteUsers} disabled={!inviteEmails.trim() || inviteLoading || extractLoading}>
              {inviteLoading ? <LoadingSpinner size="sm" /> : "Send Invitations"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Team</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the team "{selectedGroup?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteModal(false)} disabled={isLoading}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteGroup} disabled={isLoading}>
              {isLoading ? <LoadingSpinner size="sm" /> : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Groups;
